import React, { useEffect, useState, useRef } from 'react';
import Modal from 'react-modal';
import { Box, Typography, Button, IconButton } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';

const customStyles = {
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    zIndex: 2300
  },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    height: '720px',
    width: '1000px',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
  },
};

const ImageUploadModal = ({ 
  isOpen, 
  onRequestClose, 
  onConfirm, 
  title = "อัพโหลดรูปภาพ",
  maxFiles = 1, // Configurable max files
  acceptedFileTypes = ['image/*'], // Configurable file types
  maxFileSize = 2 * 1024 * 1024 // 2MB default max file size
}) => {
  const [imagePreviews, setImagePreviews] = useState([]);
  const [pastedFiles, setPastedFiles] = useState([]);

  const validateFile = (file) => {
    if (file.size > maxFileSize) {
      toast.error(`ไฟล์ ${file.name} มีขนาดใหญ่เกินไป (สูงสุด ${maxFileSize / 1024 / 1024}MB)`);
      return false;
    }
    if (pastedFiles.length >= maxFiles) {
      toast.error(`สามารถอัพโหลดได้สูงสุด ${maxFiles} ไฟล์`);
      return false;
    }
    return true;
  };

  const handlePaste = (event) => {
    if (event.clipboardData && event.clipboardData.items) {
      const items = event.clipboardData.items;
      const newFiles = [];
      const newPreviews = [];

      // Check if adding new files would exceed the limit
      if (pastedFiles.length > maxFiles - 1) {
        toast.error(`สามารถอัพโหลดได้สูงสุด ${maxFiles} ไฟล์`);
        return;
      }

      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') === 0) {
          const file = items[i].getAsFile();
          if (file && validateFile(file)) {
            newFiles.push(file);
            const reader = new FileReader();
            reader.onload = (e) => {
              newPreviews.push(e.target.result);
              if (newPreviews.length === newFiles.length) {
                setImagePreviews((prev) => [...prev, ...newPreviews]);
                setPastedFiles((prev) => [...prev, ...newFiles]);
              }
            };
            reader.readAsDataURL(file);
          }
        }
      }
    }
  };

  const handleRemoveImage = (index) => {
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
    setPastedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Only listen for paste events when modal is open
  useEffect(() => {
    if (isOpen) {
      setImagePreviews([]);
      setPastedFiles([]);
    }
  }, [isOpen]);

  // Separate effect for paste listener - needs to update when pastedFiles changes for validation
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('paste', handlePaste);
      return () => {
        document.removeEventListener('paste', handlePaste);
      };
    }
  }, [isOpen, pastedFiles.length]);

  const handleConfirm = () => {
    const filesToSend = [...pastedFiles];
    setImagePreviews([]);
    setPastedFiles([]);
    onConfirm(filesToSend);
    onRequestClose();
  };

  const handleCancel = () => {
    setImagePreviews([]);
    setPastedFiles([]);
    onRequestClose();
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={onRequestClose} style={customStyles} ariaHideApp={false}>
      <Box p={3}>
        <Typography variant="h5" gutterBottom>
          {title}
        </Typography>
        
        {pastedFiles.length === 0 && (
          <Box 
            sx={{ 
              border: '2px dashed #ccc',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              mb: 2,
            minHeight: '200px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
          <Typography>
            กด Ctrl+V เพื่อวางรูปภาพ
          </Typography>
          <Typography variant="caption" color="text.secondary">
            รองรับไฟล์รูปภาพสูงสุด {maxFiles} ไฟล์ (ไฟล์ละไม่เกิน {maxFileSize / 1024 / 1024}MB)
          </Typography>
          {pastedFiles.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              อัพโหลดแล้ว {pastedFiles.length} จาก {maxFiles} ไฟล์
            </Typography>
          )}
          </Box>
        )}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
          gap: 2,
          mb: 3
        }}>
          {imagePreviews.map((preview, index) => (
            <Box key={index} sx={{ position: 'relative' }}>
              <img 
                src={preview} 
                alt={`Uploaded ${index}`} 
                style={{ 
                  width: '100%',
                  height: '400px',
                  objectFit: 'cover',
                  borderRadius: '8px'
                }} 
              />
              <IconButton
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  bgcolor: 'rgba(0,0,0,0.5)',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                }}
                onClick={() => handleRemoveImage(index)}
              >
                <DeleteIcon sx={{ color: 'white' }} />
              </IconButton>
            </Box>
          ))}
        </Box>

        <Box display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirm}
            disabled={!imagePreviews.length}
          >
            ยืนยัน
          </Button>
          <Button
            variant="outlined"
            onClick={handleCancel}
          >
            ยกเลิก
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ImageUploadModal;